<template lang="pug">
.RoutinePlayerPage
  km-character(:message="message")
  km-step-indicator(
    class="indicator"
    :totalCount="sceneTotalCount"
    :currentCount="sceneCount"
  )
  .content
    .scene(v-if="stage")
      .stepTitle
        | {{ sceneTitle || ' ' }}
      km-keyboard-input.keyboard(
        :sceneId="`${sceneCount}`"
        :targetText="keyboardInputSettings.targetText"
        :targetKey="keyboardInputSettings.targetKey"
        :testRepeatCount="keyboardInputSettings.testRepeatCount"
        :isHandHidden="keyboardInputSettings.isHandHidden"
        :handType="keyboardInputSettings.handType"
        :isHomePositionVisibled="keyboardInputSettings.isHomePositionVisibled"
        :targetColorEnabled="keyboardInputSettings.targetColorEnabled"
        @onSpaceKeyDown="handleSpaceKeyDown"
        @onInputFinished="handleInputFinished"
      )
      .image(v-if="currentScene.imageUrl")
        img(:src="currentScene.imageUrl")

  .skipRecommendMessage(v-show="isSceneFinished")
    | スペースキーで次の画面に進みます
  km-routine-footer.footer(
    title="ルーティーン"
    backPath="/routine/top"
  )

  transition(name="fade")
    .Overlay(v-if="isOpening")
      .inner
        .title {{ stage.title }}

  transition(name="fade")
    .Overlay.Overlay--clear(v-if="isEnding")
      .inner
        .title {{ stage.title }} クリア！

  .debug(v-if="constants.IS_DEBUG")
    .forceEndButton(@click="handleForceEndButtonClick") 強制クリア

</template>

<script>
import * as _ from 'lodash'
import { mapState, mapActions } from 'vuex'
import { VueTyper } from 'vue-typer'
import soundService from '@/services/SoundService'
import { delay } from '@/helper'
import RoutineFooter from '@/components/shared/RoutineFooter'
import StepIndicator from '@/components/shared/StepIndicator'
import KeyboardInput from '@/components/shared/KeyboardInput'
import Character from '@/components/shared/Character'
import * as constants from '@/constants'

export default {
  name: 'routine-player-page',
  components: {
    'vue-typer': VueTyper,
    'km-routine-footer': RoutineFooter,
    'km-step-indicator': StepIndicator,
    'km-keyboard-input': KeyboardInput,
    'km-character': Character
  },
  data() {
    return {
      constants,

      isOpening: false,
      isEnding: false,

      isSceneFinished: false,
      isInputFinished: false,

      stage: null,
      sceneIndex: 0,
      sceneCount: 0,

      sceneTitle: '',
      message: '',
      keyboardInputSettings: {},

      nextSceneTimer: null
    }
  },
  watch: {
    stepIndex() {
      this.runPractice()
    },
    sceneIndex() {
      this.initScene()
    }
  },
  computed: {
    ...mapState('User', {
      exp: state => state.exp
    }),
    title: function() {
      return (
        this.stage &&
        `${this.stage.areaId}-${this.stage.stageId} ${this.stage.title}`
      )
    },
    currentScene: function() {
      return this.stage ? this.stage.scenes[this.sceneIndex] : {}
    },
    sceneTotalCount: function() {
      return this.stage && this.stage.scenes
        ? _.reject(this.stage.scenes, { type: 'feedback' }).length
        : 1
    }
  },
  methods: {
    ...mapActions('User', ['addExperience', 'addProgress', 'addRoutineRecord']),
    async loadScenario() {
      const settings = await (
        await fetch('/app/static/json/settings.json', { cache: 'no-store' })
      ).json()
      await fetch(`/app/static/json/routine/${settings.routine}.json`, { cache: 'no-store' })
        .then(async response => {
          const stage = await response.json()
          this.stage = stage
        })
        .catch(() => {
          alert('問題読み込みエラー')
          if (!this.embedded) this.$router.push('/')
        })
    },
    async startOpening() {
      this.isOpening = true
      await delay(1500)
      this.isOpening = false
    },
    setLevel() {
      // const isDing = this.exp + this.stage.exp >= 100
      this.addExperience(this.stage.exp)
    },
    async addRecord() {
      await this.addRoutineRecord()
    },
    async startEnding() {
      this.isEnding = true
      this.setLevel()
      await this.addRecord()
      await delay(1500)
      if (this.$route.name === 'routine-player-page') {
        this.$router.push('/routine/result')
      }
    },
    initScene() {
      this.isSceneFinished = false
      if (this.currentScene.title) this.sceneTitle = this.currentScene.title
      if (this.currentScene.message) this.message = this.currentScene.message
      if (this.currentScene.keyboardInputSettings) {
        this.$set(
          this.keyboardInputSettings,
          'targetText',
          this.currentScene.keyboardInputSettings.targetText
        )
        this.$set(
          this.keyboardInputSettings,
          'targetKey',
          this.currentScene.keyboardInputSettings.targetKey
        )
        this.$set(
          this.keyboardInputSettings,
          'testRepeatCount',
          this.currentScene.keyboardInputSettings.testRepeatCount
        )
        this.$set(
          this.keyboardInputSettings,
          'handType',
          this.currentScene.keyboardInputSettings.handType
        )
        this.$set(
          this.keyboardInputSettings,
          'isHandHidden',
          this.currentScene.keyboardInputSettings.isHandHidden
        )
        this.$set(
          this.keyboardInputSettings,
          'isHomePositionVisibled',
          this.currentScene.keyboardInputSettings.isHomePositionVisibled
        )
        this.$set(
          this.keyboardInputSettings,
          'targetColorEnabled',
          this.currentScene.keyboardInputSettings.targetColorEnabled
        )
      }

      switch (this.currentScene.type) {
        case 'singleKeyTest':
        case 'textTest':
          this.sceneCount += 1
          break
        case 'feedback':
          this.isSceneFinished = true
          this.nextSceneTimer = setTimeout(() => {
            this.nextScene()
          }, this.currentScene.endDelay || 1000)
      }
    },
    nextScene() {
      if (this.isEnding) return
      clearTimeout(this.nextSceneTimer)
      this.nextSceneTimer = null

      const isStageFinished = _.isEmpty(this.stage.scenes[this.sceneIndex + 1])
      if (isStageFinished) {
        this.startEnding()
        return
      }
      this.sceneIndex += 1
    },
    handleOKButtonClick() {
      this.sceneIndex += 1
    },
    handleNextButtonClick() {
      this.stepIndex += 1
    },
    handleSpaceKeyDown() {
      if (!this.isSceneFinished) return
      this.nextScene()
    },
    handleInputFinished() {
      this.isInputFinished = true
      this.isSceneFinished = true
      this.nextSceneTimer = setTimeout(() => {
        this.nextScene()
      }, this.currentScene.endDelay || 1000)
    },
    handleForceEndButtonClick() {
      this.startEnding()
    },
  },

  async mounted() {
    await this.loadScenario()
    await this.startOpening() // show openning
    this.initScene()

    setTimeout(() => {
      document.addEventListener('click', this.nextMessage)
    })
  },
  beforeRouteEnter(to, from, next) {
    next()
  },
  beforeRouteUpdate(to, from, next) {
    next()
  },
  destroyed() {
    soundService.stopSpeech()
    document.removeEventListener('click', this.nextMessage)
  }
}
</script>

<style lang="scss" scoped>
.RoutinePlayerPage {
  height: 100%;
  width: 100%;
}

.content {
  padding: 0;
  height: calc(100% - 80px);
  text-align: center;
  overflow: hidden;

  .scene {
    height: 100%;
    background-size: cover;
    background-position: center bottom;
    background-image: url(../assets/bg_challenge.jpg);

    .stepTitle {
      margin: 0 0 20px 0;
      padding: 30px;
      height: 100px;
      background: rgba(0, 0, 0, 0.9);
      color: #fff;
      font-size: 3rem;
    }

    .keyboard {
    }
  }
}

.indicator {
  position: absolute;
  display: flex;
  bottom: 68px;
  left: 30%;
  z-index: 50;
}

.Overlay {
  position: absolute;
  top: 0;
  display: flex;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 100;

  .inner {
    display: flex;
    width: 100%;
    height: 100px;
    background: rgba(#ffff, 0.95);
    color: #000;
    font-size: 3rem;
    align-items: center;
    justify-content: center;
  }

  &--clear {
    .inner {
      background: rgba(yellow, 0.95);
      color: #000;
    }
  }
}

.skipRecommendMessage {
  position: absolute;
  bottom: 0;
  left: 50%;
}

.footer {
  position: absolute;
  bottom: 0;
}

.debug {
  position: fixed;
  left: 0;
  bottom: 0;
  color: #fff;
}
</style>
